import { useState } from "react";
import { FormError } from "../../_components";
import AmityLogo from "../../_components/amity-logo.component";
import { AuthService } from "../../_service/auth.service";
import "../../_styles/color/light-color.css";

/**
 * Amity SSO login: email + password.
 *
 * Submits credentials to the backend proxy (`POST auth/amity/login`), which
 * validates them against Amity's token service (besocialapp.php) and returns an
 * auth token. We then hand that token to the existing `?token=` login flow
 * (see App.js -> loadUserProfileFromToken), which fetches the profile and
 * establishes the session.
 *
 * UI follows the beSocial login mock-ups (web: two-panel card, mobile: stacked
 * hero) supplied by Amity — see Drive folder "beSocial Login pages".
 */

// brand colors sampled from the mock-ups
const NAVY = "#002E5E";
const YELLOW = "#FFC907";

export default function AmityAuthComponent({
  community = null,
  communityDetail = null,
  setErrorMessage = (e) => {},
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isFormSubmitable =
    email.trim().length > 0 && password.length > 0 && !isLoading;

  const login = async () => {
    setError("");

    if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(email.trim())) {
      return setError("Please enter a valid email.");
    }

    setIsLoading(true);
    try {
      const { token } = await AuthService.amityLogin(email.trim(), password);

      if (!token) {
        setIsLoading(false);
        return setError("Invalid email or password.");
      }

      // hand the token to the existing token-login flow (App.js), returning
      // the user to where they came from (e.g. a shared event link) instead
      // of always landing on the homepage
      const params = new URLSearchParams(window.location.search);
      const redirectParam = params.get("redirect");
      // same-origin relative paths only — never redirect off-site
      let destination =
        redirectParam &&
        redirectParam.startsWith("/") &&
        !redirectParam.startsWith("//")
          ? redirectParam
          : null;
      if (!destination) {
        // the unauth router renders this login page at whatever URL the
        // visitor originally hit, so the current path IS the deep link
        const { pathname, search } = window.location;
        if (/^\/(login|register)/.test(pathname) || pathname === "/") {
          destination = "/";
        } else {
          const query = new URLSearchParams(search);
          query.delete("error");
          query.delete("token");
          destination =
            pathname + (query.toString() ? "?" + query.toString() : "");
        }
      }
      const sep = destination.includes("?") ? "&" : "?";
      window.location.href =
        window.location.origin +
        destination +
        sep +
        "token=" +
        encodeURIComponent(token);
    } catch (err) {
      setIsLoading(false);
      const message =
        (err && err.response && err.response.data && err.response.data.message) ||
        "Invalid email or password.";
      setError(message);
    }
  };

  const detail = communityDetail ?? community;
  const termsLink =
    detail?.termsAndConditionsLink ?? "https://amityonline.com/terms-of-use";
  const privacyLink =
    detail?.privacyPolicyLink ?? "https://amityonline.com/privacy-policy";

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white md:p-6">
      <div className="flex w-full flex-col overflow-hidden bg-white md:h-[843px] md:w-[1020px] md:flex-row md:rounded-3xl md:shadow-[0_12px_40px_rgba(16,35,67,0.14)]">
        {/* collage panel: left half on web, top hero on mobile */}
        <div className="relative h-[44vh] min-h-[300px] w-full shrink-0 md:h-auto md:w-[500px]">
          <img
            src="/assets/besocial/login-collage.jpg"
            alt="Amity University Online learner community"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 flex flex-col justify-end px-8 pb-10 md:justify-center md:px-10 md:pb-0">
            <div className="text-[32px] font-normal leading-[1.45] text-white md:text-[40px] md:leading-[1.5]">
              Official learner
              <br />
              community of
              <span className="block font-bold" style={{ color: YELLOW }}>
                Amity University Online
              </span>
            </div>
          </div>
        </div>

        {/* form panel */}
        <div className="flex w-full flex-1 flex-col items-center px-6 pb-10 pt-8 md:px-[60px] md:pt-[78px]">
          <img
            src="/assets/besocial/besocial-logo.png"
            alt="beSocial — Connect. Create. Collaborate"
            className="w-[170px] md:w-[293px]"
          />
          <div className="mt-4 md:hidden" style={{ color: NAVY }}>
            <AmityLogo height={44} />
          </div>
          <div className="mt-6 hidden md:block" style={{ color: NAVY }}>
            <AmityLogo height={64} />
          </div>

          <div className="mt-8 w-full max-w-[400px] md:mt-12">
            <label
              htmlFor="besocial-email"
              className="block pb-2 text-[15px] font-semibold"
              style={{ color: NAVY }}>
              Email ID
            </label>
            <input
              id="besocial-email"
              type="email"
              placeholder="Enter email ID"
              autoComplete="username"
              className="h-[46px] w-full rounded-lg border border-[#E8E8E8] bg-white px-4 text-[15px] text-gray-900 outline-none placeholder:text-[#A2A2A4] focus:border-[#002E5E]"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
            />

            <label
              htmlFor="besocial-password"
              className="mt-5 block pb-2 text-[15px] font-semibold"
              style={{ color: NAVY }}>
              Password
            </label>
            <input
              id="besocial-password"
              type="password"
              placeholder="Enter your password"
              autoComplete="current-password"
              className="h-[46px] w-full rounded-lg border border-[#E8E8E8] bg-white px-4 text-[15px] text-gray-900 outline-none placeholder:text-[#A2A2A4] focus:border-[#002E5E]"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isFormSubmitable) login();
              }}
            />

            <div className="pt-2 text-center">
              <FormError error={error} />
            </div>

            <button
              type="button"
              disabled={!isFormSubmitable}
              onClick={login}
              className="mt-9 h-[49px] w-full rounded-lg text-[16px] font-bold tracking-[0.08em] transition-opacity disabled:cursor-not-allowed"
              style={{ backgroundColor: YELLOW, color: NAVY }}>
              {isLoading ? "LOGGING IN..." : "LOGIN"}
            </button>

            <div className="pt-4 text-center text-[13px] leading-[1.55] text-black">
              Login with your official
              <br />
              <span className="font-bold">Amity Online Student Credentials</span>
            </div>

            <div className="pt-9 text-center text-[13px] leading-[1.6] tracking-[0.04em] text-[#8F8F8F]">
              “By clicking Login” above, you acknowledge that you have read and
              understood, and agree to our{" "}
              <a
                href={termsLink}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-[#6F6F6F] hover:underline">
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a
                href={privacyLink}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-[#6F6F6F] hover:underline">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
